<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  CarryForwardMode,
  FormDefinition,
  FormElementBase,
  WorkflowDefinition,
} from "~/api";
import {
  buildVersionInfo,
  previousVersionOf,
  siblingVersionsOf,
} from "~/NewFormBuilder/composables/useFormVersions";
import type { Slotted } from "~/types/Slotted";
import { isQuestionElement } from "~/utils/formUtils";
import { splitByCaps } from "~/utils/miscUtils";

const form = defineModel<FormDefinition>("form", { required: true });

const props = defineProps<{
  workflowDefinition: WorkflowDefinition;
  readOnly?: boolean;
}>();

// Per-question choice for what happens to this question's answer when a workflow's form is
// upgraded to this version. Presented as three clear options (mapped to CarryForwardMode).
const carryForwardOptions = [
  {
    value: CarryForwardMode.SAVE_SEPARATE,
    label: "Copy forward",
  },
  {
    value: CarryForwardMode.OVERWRITE_ORIGINAL,
    label: "Keep in sync",
  },
  {
    value: CarryForwardMode.DO_NOT_CARRY_FORWARD,
    label: "Don't copy",
  },
];

const allForms = computed<FormDefinition[]>(
  () => props.workflowDefinition.formDefinitions ?? [],
);

const siblings = computed(() => siblingVersionsOf(form.value, allForms.value));

const otherVersions = computed(() =>
  siblings.value.filter((fd) => fd.guid !== form.value.guid),
);

const hasOtherVersions = computed(() => otherVersions.value.length > 0);

const versionInfo = computed(() => buildVersionInfo(allForms.value));

function versionLabel(fd: FormDefinition): string {
  const info = versionInfo.value.get(fd.guid);
  const tags: string[] = [];
  if (info?.isActive) tags.push("Active");
  if (info?.isDefault) tags.push("Default");
  const suffix = tags.length ? ` (${tags.join(", ")})` : "";
  return `v${info?.versionNumber ?? "?"} — ${fd.name}${suffix}`;
}

const selectedGuid = ref<string>();

// Default the comparison to the immediately previous version.
watch(
  () => form.value.guid,
  () => {
    selectedGuid.value = previousVersionOf(form.value, allForms.value)?.guid;
  },
  { immediate: true },
);

const selectedVersion = computed(() =>
  otherVersions.value.find((fd) => fd.guid === selectedGuid.value),
);

interface DiffRow {
  questionCode: string;
  /** The element in THIS version, if present (editable). */
  current?: FormElementBase;
  /** The element in the compared version, if present. */
  other?: FormElementBase;
  status: "matched" | "added" | "removed";
}

/** Ordered question elements of a form (form order, questions only, non-archived). */
function orderedQuestions(fd: FormDefinition | undefined): FormElementBase[] {
  return (fd?.components ?? []).filter(
    (c) => !c.isArchived && isQuestionElement(c.type),
  );
}

const diffRows = computed<DiffRow[]>(() => {
  const currentQs = orderedQuestions(form.value);
  const otherQs = orderedQuestions(selectedVersion.value);
  const otherByCode = new Map(otherQs.map((q) => [q.questionCode, q]));
  const currentCodes = new Set(currentQs.map((q) => q.questionCode));

  const rows: DiffRow[] = [];

  // Precompute, for each removed question, the shared question it should follow in the OTHER
  // form, so removed rows slot into roughly their original position.
  const removedAfter = new Map<string, string[]>();
  const leadingRemoved: string[] = [];
  let lastSharedInOther: string | undefined;
  for (const q of otherQs) {
    if (currentCodes.has(q.questionCode)) {
      lastSharedInOther = q.questionCode;
    } else if (lastSharedInOther === undefined) {
      leadingRemoved.push(q.questionCode);
    } else {
      const list = removedAfter.get(lastSharedInOther) ?? [];
      list.push(q.questionCode);
      removedAfter.set(lastSharedInOther, list);
    }
  }

  for (const code of leadingRemoved) {
    rows.push({
      questionCode: code,
      other: otherByCode.get(code),
      status: "removed",
    });
  }

  for (const q of currentQs) {
    const inOther = otherByCode.get(q.questionCode);
    rows.push({
      questionCode: q.questionCode,
      current: q,
      other: inOther,
      status: inOther ? "matched" : "added",
    });
    for (const removedCode of removedAfter.get(q.questionCode) ?? []) {
      rows.push({
        questionCode: removedCode,
        other: otherByCode.get(removedCode),
        status: "removed",
      });
    }
  }

  return rows;
});

const counts = computed(() => ({
  matched: diffRows.value.filter((r) => r.status === "matched").length,
  added: diffRows.value.filter((r) => r.status === "added").length,
  removed: diffRows.value.filter((r) => r.status === "removed").length,
}));

function rowClass({ row }: { row: DiffRow }) {
  return `diff-${row.status}`;
}

// --- Carry-forward editing (this version's questions) ---

function carryForwardOf(el: FormElementBase): CarryForwardMode | undefined {
  return el.properties?.carryForwardMode ?? undefined;
}

function setCarryForward(el: FormElementBase, value?: CarryForwardMode) {
  if (!el.properties) return;
  // Mutating the shared component object flows through the designer's existing save path.
  // Clearing (undefined) means "default" = Overwrite Original on the server.
  el.properties.carryForwardMode = value;
}

function setAll(value?: CarryForwardMode) {
  for (const row of diffRows.value) {
    if (row.current) setCarryForward(row.current, value);
  }
}

// A real ref (not a write-only computed) so the bulk select shows the last-applied value instead
// of snapping back to the placeholder.
const bulkValue = ref<CarryForwardMode | undefined>(undefined);
watch(bulkValue, (v) => setAll(v));
</script>

<template>
  <div class="versions-tab">
    <div class="versions-header">
      <div v-if="hasOtherVersions" class="compare-controls">
        <span>Compare with version:</span>
        <el-select v-model="selectedGuid" style="width: 340px">
          <el-option
            v-for="fd in otherVersions"
            :key="fd.guid"
            :label="versionLabel(fd)"
            :value="fd.guid"
          />
        </el-select>
        <div class="legend">
          <el-tag type="success" size="small"
            >{{ counts.matched }} matched</el-tag
          >
          <el-tag type="warning" size="small">{{ counts.added }} added</el-tag>
          <el-tag type="danger" size="small"
            >{{ counts.removed }} removed</el-tag
          >
        </div>
      </div>

      <div v-if="!props.readOnly && diffRows.length" class="bulk-controls">
        <span>Set for all questions:</span>
        <el-select
          v-model="bulkValue"
          placeholder="Choose…"
          clearable
          style="width: 220px"
          @clear="setAll(undefined)"
        >
          <el-option
            v-for="opt in carryForwardOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
      </div>

      <p class="versions-help">
        Questions are in this version's form order. Choose what happens to each
        answer when a form is upgraded to this version, matched by Question
        Code:
        <strong>Copy forward</strong> copies the previous version's answer once
        (then independent), <strong>Keep in sync</strong> shares one value
        across versions, <strong>Don't copy</strong> starts blank.
        <template v-if="hasOtherVersions">
          <strong>Added</strong> exist only in this version;
          <strong>removed</strong> existed only in the compared version.
        </template>
      </p>
    </div>

    <el-table
      :data="diffRows"
      :row-class-name="rowClass"
      size="small"
      style="width: 100%"
    >
      <el-table-column label="This version" min-width="240">
        <template #default="{ row }: Slotted<DiffRow>">
          <template v-if="row.current">
            <code>{{ row.current.questionCode }}</code>
            <span class="label">{{ row.current.label || "" }}</span>
            <span class="type">{{ splitByCaps(row.current.type) }}</span>
          </template>
          <span v-else class="absent">—</span>
        </template>
      </el-table-column>

      <el-table-column label="On upgrade" width="250">
        <template #default="{ row }: Slotted<DiffRow>">
          <el-select
            v-if="row.current"
            :model-value="carryForwardOf(row.current)"
            :disabled="props.readOnly"
            placeholder="Keep in sync (default)"
            clearable
            style="width: 100%"
            @update:model-value="
              (v?: CarryForwardMode) => setCarryForward(row.current!, v)
            "
          >
            <el-option
              v-for="opt in carryForwardOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <span v-else class="absent">—</span>
        </template>
      </el-table-column>

      <el-table-column
        v-if="hasOtherVersions"
        label=""
        width="56"
        align="center"
      >
        <template #default="{ row }: Slotted<DiffRow>">
          <span v-if="row.status === 'matched'" title="In both versions"
            >=</span
          >
          <span v-else-if="row.status === 'added'" title="Only in this version"
            >+</span
          >
          <span v-else title="Only in the compared version">−</span>
        </template>
      </el-table-column>

      <el-table-column
        v-if="hasOtherVersions"
        label="Compared version"
        min-width="240"
      >
        <template #default="{ row }: Slotted<DiffRow>">
          <template v-if="row.other">
            <code>{{ row.other.questionCode }}</code>
            <span class="label">{{ row.other.label || "" }}</span>
            <span class="type">{{ splitByCaps(row.other.type) }}</span>
          </template>
          <span v-else class="absent">—</span>
        </template>
      </el-table-column>
    </el-table>

    <el-empty
      v-if="!diffRows.length"
      description="This form has no questions yet."
    />
  </div>
</template>

<style scoped lang="less">
.versions-tab {
  padding: 0 1em 1em;
  // The designer drawer caps content height; give this panel its own scroll.
  max-height: calc(100vh - 160px);
  overflow-y: auto;
}
.versions-header {
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  margin-bottom: 1em;
}
.compare-controls {
  display: flex;
  align-items: center;
  gap: 1em;
  flex-wrap: wrap;
}
.bulk-controls {
  display: flex;
  align-items: center;
  gap: 0.75em;
}
.legend {
  display: flex;
  gap: 0.5em;
}
.versions-help {
  color: var(--el-text-color-secondary);
  margin: 0;
}
code {
  font-family: monospace;
  margin-right: 8px;
}
.label {
  margin-right: 8px;
}
.type {
  color: var(--el-text-color-secondary);
  font-size: 0.85em;
}
.absent {
  color: var(--el-text-color-placeholder);
}
:deep(.diff-added) {
  background: var(--el-color-warning-light-9);
}
:deep(.diff-removed) {
  /* Light red (not gray) — gray collides with the table's row-hover highlight and reads as noise. */
  background: var(--el-color-danger-light-9);
}
</style>
